import { APP_TITLE } from '@/config/appConfig';

export function setPageTitle(title: string): void {
  if (!title || title.includes(APP_TITLE)) {
    document.title = title || APP_TITLE;
  } else {
    document.title = `${title}-${APP_TITLE}`;
  }
}

/** 判断元素是否通过 CSS display:none 隐藏（不检测 visibility/opacity） */
export function isHidden(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  return style.display === 'none';
}

/**
 * 异步加载图片，设置跨域属性以支持 canvas 导出。
 * @returns 加载成功时 resolve HTMLImageElement，失败时 reject
 */
export const loadImageAsync = (url: string) => {
  const image: HTMLImageElement = new window.Image();
  image.setAttribute('crossOrigin', 'Anonymous');
  return new Promise((resolve, reject) => {
    image.onload = () => {
      resolve(image);
    };
    image.onerror = reject;
    image.src = url;
  });
};

/**
 * 复制文本到剪贴板。
 * 优先使用 Clipboard API，非 HTTPS 环境降级到 execCommand 兼容方案。
 */
export const copyText = (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise<void>((resolve, reject) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('Copy');
      document.body.removeChild(textarea);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * 下载远程文件。
 *
 * 浏览器对跨域 URL（如 OSS）会忽略 <a download> 属性，直接导航跳转。
 * 解决方式：先用 fetch 把内容拉成 Blob，再构造同源 Blob URL 触发下载。
 * 拉取期间抛出异常（网络错误、CORS 等）时向上透传，由调用方决定如何提示。
 */
export const downloadUrlFile = async (
  url: string,
  fileName: string
): Promise<void> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`下载失败：${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    // 延迟释放，确保浏览器已发起下载
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  }
};

/**
 * 将字符串数据转为 Blob URL 并触发下载，下载完成后立即释放 URL 对象防止内存泄漏。
 * 适用于接口直接返回文件内容（非 URL）的场景。
 */
export function download(data: string, fileName: string) {
  if (!data) {
    return;
  }
  console.log(data);
  const url = window.URL.createObjectURL(new Blob([data]));
  let link: HTMLAnchorElement | null = document.createElement('a');
  link.style.display = 'none';
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(link.href);
  document.body.removeChild(link);
  link = null;
}
